import { Alert, Button, Dropdown, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useChecklistItemCtx, useChecklistStep } from '../../../../../components/checklist';
import type { DropdownOption } from '../../../../../components/ui/dropdown/types';
import { useHandlers } from '../../../../../context/page-context';
import { useCustomerGroups } from '../../../../customer-groups';
import { PRICE_LIST_SOURCES, type PriceList, useAllPriceLists } from '../../../../price-lists';
import { StepWithPreview } from '../../../shared/components/step-with-preview';
import type { OnboardingChecklistStep } from '../../../shared/types';
import { PRICE_LIST_STEP } from '../price-list/checklist-item';
import { AssignmentGraphic } from './assignment-graphic';
import { useAssignPriceList } from './use-assign-price-list';
import { useChecklistStatus } from './use-checklist-status';

export const PRICE_LIST_ASSIGNMENT_STEP: OnboardingChecklistStep = {
  icon: 'tag',
  id: 'price-list-assignment',
  title: t('features:onboarding.components.price_list_assignment_item.title'),
  useStatus: useChecklistStatus,
  children: <AssignPriceListBody />,
};

// Platform price lists can't be assigned to a group (they're store-managed), matching the group builder.
function isAssignablePriceListOption(priceList: PriceList): boolean {
  return priceList.source !== PRICE_LIST_SOURCES.PLATFORM;
}

// A selector with exactly one option is pre-filled and disabled — nothing to choose — so we seed the
// selection with it; otherwise the user picks. Empty when there's no single default.
function soleOption(options: DropdownOption[]): string {
  return options.length === 1 ? (options[0]?.value ?? '') : '';
}

function AssignPriceListBody() {
  const { status } = useChecklistItemCtx();

  return (
    <StepWithPreview preview={<AssignmentGraphic />}>
      {status === 'complete' ? <AssignComplete /> : <AssignForm />}
    </StepWithPreview>
  );
}

function AssignForm() {
  // Coordinate with the price-list setup step: when it shares this checklist we
  // can send the user there; standalone, we fall back to the form's own empty-state.
  const priceListStep = useChecklistStep(PRICE_LIST_STEP.id);
  const { navigationHandler } = useHandlers();

  const { data: groups } = useCustomerGroups();
  const { data: priceLists } = useAllPriceLists();
  const { assign, isPending, isError } = useAssignPriceList();

  const groupOptions: DropdownOption[] = groups.map((group) => ({
    value: group.slug,
    label: group.name,
  }));

  const priceListOptions: DropdownOption[] = priceLists
    .filter(isAssignablePriceListOption)
    .map((priceList) => ({
      value: priceList.slug,
      label: `${priceList.currency_code.toUpperCase()} - ${priceList.name}`,
    }));

  const [selectedGroup, setSelectedGroup] = useState<string>(() => soleOption(groupOptions));
  const [selectedPriceList, setSelectedPriceList] = useState<string>(() =>
    soleOption(priceListOptions),
  );

  // Assigning needs a price list to exist first: route to the setup step if present, else the page.
  if (priceListOptions.length === 0) {
    const onClick = priceListStep.exists
      ? () => priceListStep.open()
      : () => {
          navigationHandler.navigateById('pricelist.create');
        };

    return (
      <>
        <Text>
          {t('features:onboarding.components.price_list_assignment_item.setup_price_list_prompt')}
        </Text>
        <div>
          <Button variant="primary" icon="priceList" onClick={onClick}>
            {t('features:onboarding.components.price_list_assignment_item.no_price_lists')}
          </Button>
        </div>
      </>
    );
  }

  const canAssign = !!selectedGroup && !!selectedPriceList && !isPending;

  return (
    <>
      <Text>{t('features:onboarding.components.price_list_assignment_item.description')}</Text>
      <div className="flex flex-row flex-nowrap gap-3">
        <div className="flex-1">
          <Dropdown
            label={t('features:onboarding.components.price_list_assignment_item.group_label')}
            options={groupOptions}
            value={selectedGroup}
            placeholder={t(
              'features:onboarding.components.price_list_assignment_item.group_placeholder',
            )}
            disabled={groupOptions.length <= 1}
            onChange={setSelectedGroup}
          />
        </div>
        <div className="flex-1">
          <Dropdown
            label={t('features:onboarding.components.price_list_assignment_item.price_list_label')}
            options={priceListOptions}
            value={selectedPriceList}
            placeholder={t(
              'features:onboarding.components.price_list_assignment_item.price_list_placeholder',
            )}
            noOptionsMessage={t(
              'features:onboarding.components.price_list_assignment_item.no_price_lists',
            )}
            disabled={priceListOptions.length <= 1}
            onChange={setSelectedPriceList}
          />
        </div>
      </div>
      {isError && (
        <Alert variant="error" headingLevel="h3">
          {t('features:onboarding.components.price_list_assignment_item.error_generic')}
        </Alert>
      )}
      <div>
        <Button
          variant="primary"
          loading={isPending}
          disabled={!canAssign}
          onClick={() =>
            selectedGroup && selectedPriceList && assign(selectedGroup, selectedPriceList)
          }
        >
          {t('features:onboarding.components.price_list_assignment_item.assign')}
        </Button>
      </div>
    </>
  );
}

function AssignComplete() {
  const { navigationHandler } = useHandlers();

  return (
    <>
      <Text>{t('features:onboarding.components.price_list_assignment_item.complete')}</Text>
      <div>
        <Button
          variant="secondary"
          icon="tag"
          onClick={() => navigationHandler.navigate('/customers/groups')}
        >
          {t('features:onboarding.components.price_list_assignment_item.manage')}
        </Button>
      </div>
    </>
  );
}
